import { useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { SectionHeader } from "../shared/SectionHeader";
import { SettingsInput, labelClass } from "../shared/SettingsInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UserAvatar } from "@/components/UserAvatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PASSWORD_REQUIREMENTS, validatePassword } from "@/lib/password";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_AVATAR_PX = 512;
const CROP_BOX_SIZE = 320;
const MIN_SCALE = 0.5;
const MAX_SCALE = 4;
const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

type CropState = {
  url: string;
  file: File;
  imgW: number;
  imgH: number;
  scale: number;
  offsetX: number;
  offsetY: number;
};

export function EmployeeAccountSettings() {
  const { user, refreshProfile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [cropState, setCropState] = useState<CropState | null>(null);
  const cropBoxRef = useRef<HTMLDivElement>(null);
  const cropStateRef = useRef<CropState | null>(null);
  const cropDragRef = useRef<{ startX: number; startY: number; startOffsetX: number; startOffsetY: number } | null>(null);
  const pinchRef = useRef<{ initialDistance: number; initialScale: number } | null>(null);
  cropStateRef.current = cropState;
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    supabase
      .from("profiles")
      .select("full_name, phone, avatar_url")
      .eq("user_id", user.id)
      .maybeSingle()
      .then(({ data }) => {
        const row = data as { full_name?: string | null; phone?: string | null; avatar_url?: string | null } | null;
        setFullName(row?.full_name ?? "");
        setPhone(row?.phone ?? "");
        setAvatarUrl(row?.avatar_url ?? null);
      });
  }, [user?.id]);

  useEffect(() => {
    const onMouseUp = () => {
      cropDragRef.current = null;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (!cropDragRef.current || !cropState) return;
      setCropState((prev) =>
        prev
          ? {
              ...prev,
              offsetX: cropDragRef.current!.startOffsetX + (e.clientX - cropDragRef.current!.startX),
              offsetY: cropDragRef.current!.startOffsetY + (e.clientY - cropDragRef.current!.startY),
            }
          : null
      );
    };
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [cropState]);

  // Native touch handlers with passive: false so pinch-to-zoom works and page doesn't zoom (WhatsApp/Instagram style)
  useEffect(() => {
    const el = cropBoxRef.current;
    if (!el || !cropState) return;

    const touchDistance = (touches: TouchList) =>
      touches.length >= 2
        ? Math.hypot(touches[1].clientX - touches[0].clientX, touches[1].clientY - touches[0].clientY)
        : 0;

    const onTouchStart = (e: TouchEvent) => {
      const state = cropStateRef.current;
      if (!state) return;
      if (e.touches.length === 2) {
        pinchRef.current = { initialDistance: touchDistance(e.touches), initialScale: state.scale };
        cropDragRef.current = null;
      } else if (e.touches.length === 1) {
        cropDragRef.current = {
          startX: e.touches[0].clientX,
          startY: e.touches[0].clientY,
          startOffsetX: state.offsetX,
          startOffsetY: state.offsetY,
        };
        pinchRef.current = null;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      const state = cropStateRef.current;
      if (!state) return;
      if (e.touches.length === 2) {
        e.preventDefault();
        const dist = touchDistance(e.touches);
        if (dist === 0) return;
        if (!pinchRef.current) pinchRef.current = { initialDistance: dist, initialScale: state.scale };
        const ratio = dist / pinchRef.current.initialDistance;
        const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchRef.current.initialScale * ratio));
        setCropState((prev) => (prev ? { ...prev, scale: newScale } : null));
      } else if (e.touches.length === 1 && cropDragRef.current) {
        e.preventDefault();
        pinchRef.current = null;
        setCropState((prev) =>
          prev
            ? {
                ...prev,
                offsetX: cropDragRef.current!.startOffsetX + (e.touches[0].clientX - cropDragRef.current!.startX),
                offsetY: cropDragRef.current!.startOffsetY + (e.touches[0].clientY - cropDragRef.current!.startY),
              }
            : null
        );
      }
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) pinchRef.current = null;
      if (e.touches.length === 0) cropDragRef.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    el.addEventListener("touchcancel", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
      el.removeEventListener("touchcancel", onTouchEnd);
    };
  }, [cropState]);

  const connectedProviders = useMemo(() => {
    // Supabase's `User` type doesn't always include these provider fields, so we treat them as optional.
    const userWithProviders = user as unknown as {
      identities?: Array<{ provider?: string }>;
      app_metadata?: { providers?: string[] };
    };

    const identityProviders = (userWithProviders.identities ?? [])
      .map((identity) => identity.provider)
      .filter((p): p is string => Boolean(p));

    const appProviders = userWithProviders.app_metadata?.providers ?? [];
    return [...new Set([...identityProviders, ...appProviders])];
  }, [user]);

  const googleConnected = connectedProviders.includes("google");

  function getInitialCrop(imgW: number, imgH: number): { scale: number; offsetX: number; offsetY: number } {
    const scale = Math.min(CROP_BOX_SIZE / imgW, CROP_BOX_SIZE / imgH, 2);
    const scaledW = imgW * scale;
    const scaledH = imgH * scale;
    const offsetX = (CROP_BOX_SIZE - scaledW) / 2;
    const offsetY = (CROP_BOX_SIZE - scaledH) / 2;
    return { scale, offsetX, offsetY };
  }

  /** Produce cropped square blob from current crop state (Instagram-style: user chose area via pan/zoom). */
  function cropToBlob(state: CropState): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const { scale, offsetX, offsetY } = state;
        const side = CROP_BOX_SIZE / scale;
        let sx = -offsetX / scale;
        let sy = -offsetY / scale;
        sx = Math.max(0, Math.min(img.width - side, sx));
        sy = Math.max(0, Math.min(img.height - side, sy));
        const canvas = document.createElement("canvas");
        canvas.width = MAX_AVATAR_PX;
        canvas.height = MAX_AVATAR_PX;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not supported"));
          return;
        }
        ctx.drawImage(img, sx, sy, side, side, 0, 0, MAX_AVATAR_PX, MAX_AVATAR_PX);
        canvas.toBlob(
          (blob) => (blob ? resolve(blob) : reject(new Error("Failed to export image"))),
          state.file.type === "image/png" ? "image/png" : "image/jpeg",
          0.9
        );
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = state.url;
    });
  }

  const onAvatarFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user?.id) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast({ title: "Please choose a JPG, PNG or WebP image under 5MB", variant: "destructive" });
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toast({ title: "Please choose a JPG, PNG or WebP image under 5MB", variant: "destructive" });
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const { scale, offsetX, offsetY } = getInitialCrop(img.width, img.height);
      setCropState({
        url,
        file,
        imgW: img.width,
        imgH: img.height,
        scale,
        offsetX,
        offsetY,
      });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast({ title: "Could not load image", variant: "destructive" });
    };
    img.src = url;
  };

  const closeCrop = () => {
    if (cropState) URL.revokeObjectURL(cropState.url);
    setCropState(null);
  };

  const onCropMouseDown = (e: React.MouseEvent) => {
    if (!cropState) return;
    e.preventDefault();
    cropDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: cropState.offsetX,
      startOffsetY: cropState.offsetY,
    };
  };

  const onCropWheel = (e: React.WheelEvent) => {
    if (!cropState) return;
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setCropState((prev) =>
      prev ? { ...prev, scale: Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev.scale + delta)) } : null
    );
  };

  const onCropMouseMove = (e: React.MouseEvent) => {
    const d = cropDragRef.current;
    if (!d || !cropState) return;
    setCropState((prev) =>
      prev
        ? {
            ...prev,
            offsetX: d.startOffsetX + (e.clientX - d.startX),
            offsetY: d.startOffsetY + (e.clientY - d.startY),
          }
        : null
    );
  };

  const onCropApply = async () => {
    if (!cropState || !user?.id) return;
    const state = cropState;
    try {
      const blob = await cropToBlob(state);
      closeCrop();
      setUploadingAvatar(true);
      const ext = state.file.type === "image/png" ? "png" : state.file.type === "image/webp" ? "webp" : "jpg";
      const path = `${user.id}/avatar.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("avatars")
        .upload(path, blob, { upsert: true, contentType: blob.type });
      if (uploadError) throw uploadError;
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ avatar_url: path, updated_at: new Date().toISOString() })
        .eq("user_id", user.id);
      if (updateError) throw updateError;
      setAvatarUrl(path);
      await refreshProfile();
      queryClient.invalidateQueries({ queryKey: ["avatar-signed-url"] });
      toast({ title: "Profile photo updated" });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      const isBucketError = /bucket|not found|not exist/i.test(String(msg));
      toast({
        title: "Could not update photo",
        description: isBucketError
          ? "Avatar storage is not set up. Ask your admin to create a private bucket named \"avatars\" in Supabase Dashboard → Storage."
          : msg,
        variant: "destructive",
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const onRemoveAvatar = async () => {
    if (!user?.id || !avatarUrl) return;
    setUploadingAvatar(true);
    try {
      await supabase.storage.from("avatars").remove([avatarUrl]);
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null, updated_at: new Date().toISOString() })
        .eq("user_id", user.id);
      if (error) throw error;
      setAvatarUrl(null);
      await refreshProfile();
      queryClient.invalidateQueries({ queryKey: ["avatar-signed-url"] });
      toast({ title: "Profile photo removed" });
    } catch (err: unknown) {
      toast({
        title: "Could not remove photo",
        description: err instanceof Error ? err.message : "Remove failed",
        variant: "destructive",
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) return;
    const newFullName = fullName.trim() || null;
    const newPhone = phone.trim() || null;
    setSavingProfile(true);
    const { data, error } = await supabase
      .from("profiles")
      .upsert(
        {
          user_id: user.id,
          full_name: newFullName,
          phone: newPhone,
          updated_at: new Date().toISOString(),
        },
        { onConflict: "user_id" }
      )
      .select("full_name, phone")
      .single();
    setSavingProfile(false);
    if (error) {
      toast({ title: "Could not save account", description: error.message, variant: "destructive" });
      return;
    }
    const row = data as { full_name?: string | null; phone?: string | null } | null;
    // This should always be present after upsert(), but in case of a rare return-shape mismatch, refresh from DB.
    if (row) {
      setFullName(row.full_name ?? "");
      setPhone(row.phone ?? "");
    }
    await refreshProfile();
    toast({ title: "Account updated", description: "Your name will appear in the dashboard greeting." });
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validatePassword(newPassword);
    if (validationError) {
      toast({ title: "Choose a stronger password", description: validationError, variant: "destructive" });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords do not match", variant: "destructive" });
      return;
    }

    setChangingPassword(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setChangingPassword(false);
    if (error) {
      toast({ title: "Could not update password", description: error.message, variant: "destructive" });
      return;
    }
    setNewPassword("");
    setConfirmPassword("");
    toast({ title: "Password updated" });
  };

  return (
    <>
      <Dialog open={!!cropState} onOpenChange={(open) => !open && closeCrop()}>
        <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle>Crop profile photo</DialogTitle>
          </DialogHeader>
          {cropState && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Drag to move, pinch or scroll to zoom. The circle shows what will be used.
              </p>
              <div
                ref={cropBoxRef}
                className="relative mx-auto overflow-hidden rounded-full bg-muted select-none"
                style={{ width: CROP_BOX_SIZE, height: CROP_BOX_SIZE, touchAction: "none" }}
                onMouseDown={onCropMouseDown}
                onWheel={onCropWheel}
              >
                <div
                  className="absolute left-0 top-0 flex items-center justify-center"
                  style={{
                    width: cropState.imgW * cropState.scale,
                    height: cropState.imgH * cropState.scale,
                    transform: `translate(${cropState.offsetX}px, ${cropState.offsetY}px)`,
                  }}
                >
                  <img
                    src={cropState.url}
                    alt="Crop preview"
                    draggable={false}
                    className="pointer-events-none select-none"
                    style={{
                      width: cropState.imgW * cropState.scale,
                      height: cropState.imgH * cropState.scale,
                      objectFit: "cover",
                    }}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeCrop}>
                  Cancel
                </Button>
                <Button type="button" onClick={onCropApply} disabled={uploadingAvatar}>
                  {uploadingAvatar ? "Uploading…" : "Save photo"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <SectionHeader
        title="My Account"
        description="Manage your own profile details and sign-in settings."
      />
      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Profile</h3>
          <p className="text-[12px] text-muted-foreground">
            Update your name, phone number, and profile photo. You can upload a new photo or remove your current one at any time.
          </p>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-4 mb-6">
            <UserAvatar size="lg" userId={user?.id} avatarUrl={avatarUrl} fullName={fullName} />
            <div className="flex flex-col gap-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="hidden"
                onChange={onAvatarFileChange}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={uploadingAvatar}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingAvatar ? "Uploading..." : "Upload photo"}
              </Button>
              {avatarUrl && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploadingAvatar}
                  onClick={onRemoveAvatar}
                  className="text-muted-foreground hover:text-destructive hover:border-destructive"
                >
                  Delete profile photo
                </Button>
              )}
              <p className="text-[12px] text-muted-foreground">
                {avatarUrl
                  ? "You can upload a new photo or delete the current one."
                  : "Supported formats: JPG, PNG, WebP · Max size: 5MB"}
              </p>
            </div>
          </div>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div>
              <Label className={labelClass}>Full Name</Label>
              <SettingsInput value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Your full name" />
            </div>
            <div>
              <Label className={labelClass}>Phone Number</Label>
              <SettingsInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+260 97 000 0000" />
            </div>
            <Button type="submit" disabled={savingProfile}>
              {savingProfile ? "Saving..." : "Save Changes"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Change Password</h3>
          <p className="text-[12px] text-muted-foreground">
            Joined via invitation? Set a password here to sign in on other devices (e.g. phone or work computer).
            You can also use &quot;Forgot password?&quot; on the login page.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-md">
            <SettingsInput
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="New password"
            />
            <SettingsInput
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
            />
            <ul className="space-y-1 text-sm text-muted-foreground">
              {PASSWORD_REQUIREMENTS.map((requirement) => (
                <li key={requirement}>• {requirement}</li>
              ))}
            </ul>
            <Button type="submit" disabled={changingPassword}>
              {changingPassword ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="bg-card border-border">
        <CardHeader>
          <h3 className="text-[14px] font-semibold text-foreground">Connected Accounts</h3>
          <p className="text-[12px] text-muted-foreground">
            Shows which sign-in methods are connected to your account.
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-foreground">Google</span>
            <Badge variant={googleConnected ? "default" : "secondary"}>
              {googleConnected ? "Connected" : "Not connected"}
            </Badge>
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-foreground">Email and Password</span>
            <Badge variant="default">Available</Badge>
          </div>
        </CardContent>
      </Card>
    </>
  );
}
